import { createFeatureSelector, createSelector } from '@ngrx/store';
import { OnboardingState } from './onboarding.state';

// Feature selector
export const selectOnboardingState = createFeatureSelector<OnboardingState>('onboarding');

// ============================================
// Session Selectors
// ============================================
export const selectOnboardingSession = createSelector(
  selectOnboardingState,
  (state: OnboardingState) => state.session
);

export const selectOnboardingSessionId = createSelector(
  selectOnboardingSession,
  (session) => session.sessionId
);

export const selectOnboardingCurrentStep = createSelector(
  selectOnboardingSession,
  (session) => session.currentStep
);

export const selectOnboardingExpiresAt = createSelector(
  selectOnboardingSession,
  (session) => session.expiresAt
);

export const selectOnboardingSessionLoading = createSelector(
  selectOnboardingSession,
  (session) => session.isLoading
);

export const selectOnboardingSessionError = createSelector(
  selectOnboardingSession,
  (session) => session.error
);

export const selectHasValidSession = createSelector(
  selectOnboardingSession,
  (session) => {
    if (!session.sessionId || !session.expiresAt) {
      return false;
    }
    return new Date(session.expiresAt) > new Date();
  }
);

// ============================================
// Step Data Selectors
// ============================================
export const selectOnboardingStepData = createSelector(
  selectOnboardingState,
  (state: OnboardingState) => state.stepData
);

export const selectStep1Data = createSelector(
  selectOnboardingStepData,
  (stepData) => stepData.step1
);

export const selectStep2Data = createSelector(
  selectOnboardingStepData,
  (stepData) => stepData.step2
);

// ============================================
// Extraction Selectors
// ============================================
export const selectOnboardingExtraction = createSelector(
  selectOnboardingState,
  (state: OnboardingState) => state.extraction
);

export const selectExtractionStatus = createSelector(
  selectOnboardingExtraction,
  (extraction) => extraction.status
);

export const selectExtractionSource = createSelector(
  selectOnboardingExtraction,
  (extraction) => extraction.extractionSource
);

export const selectProductDescription = createSelector(
  selectOnboardingExtraction,
  (extraction) => extraction.productDescription
);

export const selectExtractionCompanyName = createSelector(
  selectOnboardingExtraction,
  (extraction) => extraction.companyName
);

export const selectExtractionBenefits = createSelector(
  selectOnboardingExtraction,
  (extraction) => extraction.benefits
);

export const selectExtractionPains = createSelector(
  selectOnboardingExtraction,
  (extraction) => extraction.pains
);

export const selectExtractionLoading = createSelector(
  selectOnboardingExtraction,
  (extraction) => extraction.isLoading
);

export const selectExtractionError = createSelector(
  selectOnboardingExtraction,
  (extraction) => extraction.error
);

export const selectIsExtractionComplete = createSelector(
  selectExtractionStatus,
  (status) => status === 'completed'
);

export const selectIsExtractionInProgress = createSelector(
  selectExtractionStatus,
  (status) => status === 'pending' || status === 'processing'
);

export const selectIsExtractionFailed = createSelector(
  selectExtractionStatus,
  (status) => status === 'failed'
);

export const selectIsExtractionDone = createSelector(
  selectExtractionStatus,
  (status) => status === 'completed' || status === 'failed'
);

// ============================================
// Update Step Selectors
// ============================================
export const selectUpdateStep = createSelector(
  selectOnboardingState,
  (state: OnboardingState) => state.updateStep
);

export const selectUpdateStepLoading = createSelector(
  selectUpdateStep,
  (updateStep) => updateStep.isLoading
);

export const selectUpdateStepSuccess = createSelector(
  selectUpdateStep,
  (updateStep) => updateStep.success
);

export const selectUpdateStepError = createSelector(
  selectUpdateStep,
  (updateStep) => updateStep.error
);

// ============================================
// Complete Onboarding Selectors
// ============================================
export const selectComplete = createSelector(
  selectOnboardingState,
  (state: OnboardingState) => state.complete
);

export const selectCompleteLoading = createSelector(
  selectComplete,
  (complete) => complete.isLoading
);

export const selectCompleteSuccess = createSelector(
  selectComplete,
  (complete) => complete.success
);

export const selectCompleteResult = createSelector(
  selectComplete,
  (complete) => complete.result
);

export const selectCompleteError = createSelector(
  selectComplete,
  (complete) => complete.error
);

// ============================================
// WebSocket Leads Selectors
// ============================================
export const selectLeads = createSelector(
  selectOnboardingState,
  (state: OnboardingState) => state.leads
);

export const selectLeadItems = createSelector(
  selectLeads,
  (leads) => leads.items
);

export const selectLeadCount = createSelector(
  selectLeadItems,
  (items) => items.length
);

export const selectIsListeningForLeads = createSelector(
  selectLeads,
  (leads) => leads.isListening
);

export const selectLeadGenerationStatus = createSelector(
  selectLeads,
  (leads) => leads.generationStatus
);

// ============================================
// Combined/Computed Selectors
// ============================================
export const selectIsAnyLoading = createSelector(
  selectOnboardingSessionLoading,
  selectUpdateStepLoading,
  selectExtractionLoading,
  selectCompleteLoading,
  (sessionLoading, updateLoading, extractionLoading, completeLoading) =>
    sessionLoading || updateLoading || extractionLoading || completeLoading
);

export const selectOnboardingErrors = createSelector(
  selectOnboardingSessionError,
  selectUpdateStepError,
  selectExtractionError,
  selectCompleteError,
  (sessionError, updateError, extractionError, completeError) => ({
    session: sessionError,
    updateStep: updateError,
    extraction: extractionError,
    complete: completeError
  })
);

// Data ready for completion: step data + extraction results
export const selectOnboardingDataForCompletion = createSelector(
  selectOnboardingStepData,
  selectProductDescription,
  selectExtractionBenefits,
  selectExtractionPains,
  (stepData, productDescription, benefits, pains) => ({
    stepData,
    productDescription,
    benefits,
    pains
  })
);
